import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';

// Types for Map Model
export interface Unit {
  id: string;
  name: string;
  description?: string;
  order: number;
  masteryState: MasteryState;
}

export interface Card {
  id: string;
  title: string;
  description?: string;
  cardType: CardType;
  packType?: PackType;
  problemCount?: number;
  status: CardStatus;
  priority: Priority;
  estimatedMinutes?: number;
  actualMinutes: number;
  isRevision: boolean;
  revisionIntervalDays?: number;
  order: number;
  dueDate?: string;
  completedAt?: string;
}

export interface Pattern {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  order: number;
  difficulty: Difficulty;
  masteryState: MasteryState;
  lastPracticedAt?: string;
  nextReviewAt?: string;
  practiceCount: number;
  units: Unit[];
  cards: Card[];
}

export interface Lane {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  order: number;
  patterns: Pattern[];
}

export interface MapData {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  order: number;
  estimatedHours?: number;
  isDefault: boolean;
  lanes: Lane[];
}

// Alias for backward compatibility
export type MapItem = MapData;

export interface Track {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  icon?: string;
  color: string;
  coverImage?: string;
  isOfficial: boolean;
  isPublic: boolean;
  trackType: 'hierarchical' | 'linear';
  mapCount?: number;
  cardCount?: number;
  maps?: MapItem[];
  cards?: Card[]; // Direct cards for linear tracks
  items?: SkillTrackItem[]; // Deprecated - use cards
  createdAt: string;
  updatedAt: string;
}

export interface SkillTrackItem {
  id: string;
  trackId: string;
  title: string;
  description?: string;
  status: CardStatus;
  estimatedMinutes?: number;
  actualMinutes: number;
  order: number;
  dueDate?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type MasteryState = 'unseen' | 'learning' | 'practicing' | 'stable' | 'mastered' | 'needs_revision';
export type CardType = 'watch' | 'read' | 'practice' | 'build' | 'revise' | 'notes' | 'flashcard' | 'custom';
export type PackType = 'easy_5' | 'medium_5' | 'hard_2' | 'mixed_10';
export type CardStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';
export type Priority = 'low' | 'medium' | 'high' | 'urgent';
export type Difficulty = 'easy' | 'medium' | 'hard';

interface MapState {
  tracks: Track[];
  currentTrack: Track | null;
  selectedMapId: string | null;
  selectedLaneId: string | null;
  selectedPatternId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: MapState = {
  tracks: [],
  currentTrack: null,
  selectedMapId: null,
  selectedLaneId: null,
  selectedPatternId: null,
  isLoading: false,
  error: null,
};

export const fetchMapTracks = createAsyncThunk(
  'map/fetchTracks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/map/tracks');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch tracks'
      );
    }
  }
);

export const fetchTrackWithTree = createAsyncThunk(
  'map/fetchTrackWithTree',
  async (trackId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/map/tracks/${trackId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch track'
      );
    }
  }
);

export const updateCardStatus = createAsyncThunk(
  'map/updateCardStatus',
  async ({ cardId, status, actualMinutes }: { cardId: string; status: CardStatus; actualMinutes?: number }, { rejectWithValue }) => {
    try {
      await api.put(`/map/cards/${cardId}`, { status, actualMinutes });
      return { cardId, status, actualMinutes };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update card'
      );
    }
  }
);

export const completeCard = createAsyncThunk(
  'map/completeCard',
  async ({ cardId, actualMinutes }: { cardId: string; actualMinutes?: number }, { rejectWithValue }) => {
    try {
      await api.post(`/map/cards/${cardId}/complete`, { actualMinutes });
      return { cardId, actualMinutes };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to complete card'
      );
    }
  }
);

export const undoCompleteCard = createAsyncThunk(
  'map/undoCompleteCard',
  async (cardId: string, { rejectWithValue }) => {
    try {
      await api.put(`/map/cards/${cardId}`, { status: 'pending' });
      return { cardId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to undo completion'
      );
    }
  }
);

export const updatePatternMastery = createAsyncThunk(
  'map/updatePatternMastery',
  async ({ patternId, masteryState }: { patternId: string; masteryState: MasteryState }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/map/patterns/${patternId}/mastery`, { masteryState });
      return { patternId, masteryState, nextReviewAt: response.data.nextReviewAt };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update mastery'
      );
    }
  }
);

// CRUD Thunks for Track
export const createTrack = createAsyncThunk(
  'map/createTrack',
  async (data: { name: string; description?: string; color?: string; trackType?: 'hierarchical' | 'linear'; isPublic?: boolean }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/map/tracks', data);
      dispatch(fetchMapTracks()); // Refresh list
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create track');
    }
  }
);

export const updateTrack = createAsyncThunk(
  'map/updateTrack',
  async ({ trackId, data }: { trackId: string; data: { name?: string; description?: string; color?: string; isPublic?: boolean } }, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/map/tracks/${trackId}`, data);
      dispatch(fetchMapTracks()); // Refresh list
      return { trackId, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update track');
    }
  }
);

export const deleteTrack = createAsyncThunk(
  'map/deleteTrack',
  async (trackId: string, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/map/tracks/${trackId}`);
      dispatch(fetchMapTracks()); // Refresh list
      return trackId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete track');
    }
  }
);

// CRUD Thunks for Map
export const createMap = createAsyncThunk(
  'map/createMap',
  async ({ trackId, data }: { trackId: string; data: { name: string; description?: string; color?: string } }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/map/tracks/${trackId}/maps`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create map');
    }
  }
);

export const updateMap = createAsyncThunk(
  'map/updateMap',
  async ({ mapId, data, trackId }: { mapId: string; data: { name?: string; description?: string; color?: string }; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/map/maps/${mapId}`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return { mapId, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update map');
    }
  }
);

export const deleteMap = createAsyncThunk(
  'map/deleteMap',
  async ({ mapId, trackId }: { mapId: string; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/map/maps/${mapId}`);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return mapId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete map');
    }
  }
);

// CRUD Thunks for Lane
export const createLane = createAsyncThunk(
  'map/createLane',
  async ({ mapId, data, trackId }: { mapId: string; data: { name: string; description?: string; color?: string }; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/map/maps/${mapId}/lanes`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create lane');
    }
  }
);

export const updateLane = createAsyncThunk(
  'map/updateLane',
  async ({ laneId, data, trackId }: { laneId: string; data: { name?: string; description?: string; color?: string }; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/map/lanes/${laneId}`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return { laneId, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update lane');
    }
  }
);

export const deleteLane = createAsyncThunk(
  'map/deleteLane',
  async ({ laneId, trackId }: { laneId: string; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/map/lanes/${laneId}`);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return laneId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete lane');
    }
  }
);

// CRUD Thunks for Pattern
export const createPattern = createAsyncThunk(
  'map/createPattern',
  async ({ laneId, data, trackId }: { laneId: string; data: { name: string; description?: string; difficulty?: Difficulty }; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/map/lanes/${laneId}/patterns`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create pattern');
    }
  }
);

export const updatePattern = createAsyncThunk(
  'map/updatePattern',
  async ({ patternId, data, trackId }: { patternId: string; data: { name?: string; description?: string; difficulty?: Difficulty }; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/map/patterns/${patternId}`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return { patternId, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update pattern');
    }
  }
);

export const deletePattern = createAsyncThunk(
  'map/deletePattern',
  async ({ patternId, trackId }: { patternId: string; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/map/patterns/${patternId}`);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return patternId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete pattern');
    }
  }
);

// CRUD Thunks for Cards (can be at any level)
export const createCard = createAsyncThunk(
  'map/createCard',
  async (
    { trackId, data, parentType, parentId }: {
      trackId: string;
      data: { title: string; description?: string; estimatedMinutes?: number; cardType?: string };
      parentType: 'track' | 'pattern';
      parentId: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const endpoint = parentType === 'pattern'
        ? `/map/patterns/${parentId}/cards`
        : `/map/tracks/${trackId}/cards`;
      const response = await api.post(endpoint, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create card');
    }
  }
);

export const updateCard = createAsyncThunk(
  'map/updateCard',
  async (
    { cardId, data, trackId }: { cardId: string; data: { title?: string; description?: string; status?: CardStatus }; trackId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await api.put(`/map/cards/${cardId}`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return { cardId, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update card');
    }
  }
);

export const deleteCard = createAsyncThunk(
  'map/deleteCard',
  async ({ cardId, trackId }: { cardId: string; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/map/cards/${cardId}`);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return cardId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete card');
    }
  }
);

// CRUD Thunks for Skill Track Items (linear tracks)
export const createSkillTrackItem = createAsyncThunk(
  'map/createSkillTrackItem',
  async ({ trackId, data }: { trackId: string; data: { title: string; description?: string; estimatedMinutes?: number } }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/map/tracks/${trackId}/items`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create item');
    }
  }
);

export const updateSkillTrackItem = createAsyncThunk(
  'map/updateSkillTrackItem',
  async ({ itemId, data, trackId }: { itemId: string; data: { title?: string; description?: string; status?: CardStatus }; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/map/skill-items/${itemId}`, data);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return { itemId, ...data };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update item');
    }
  }
);

export const deleteSkillTrackItem = createAsyncThunk(
  'map/deleteSkillTrackItem',
  async ({ itemId, trackId }: { itemId: string; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/map/skill-items/${itemId}`);
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return itemId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete item');
    }
  }
);

export const completeSkillTrackItem = createAsyncThunk(
  'map/completeSkillTrackItem',
  async ({ itemId, actualMinutes, trackId }: { itemId: string; actualMinutes?: number; trackId: string }, { rejectWithValue, dispatch }) => {
    try {
      await api.post(`/map/skill-items/${itemId}/complete`, { actualMinutes });
      dispatch(fetchTrackWithTree(trackId)); // Refresh track
      return { itemId, actualMinutes };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to complete item');
    }
  }
);

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    selectMap: (state, action: PayloadAction<string | null>) => {
      state.selectedMapId = action.payload;
      state.selectedLaneId = null;
      state.selectedPatternId = null;
    },
    selectLane: (state, action: PayloadAction<string | null>) => {
      state.selectedLaneId = action.payload;
      state.selectedPatternId = null;
    },
    selectPattern: (state, action: PayloadAction<string | null>) => {
      state.selectedPatternId = action.payload;
    },
    clearSelection: (state) => {
      state.selectedMapId = null;
      state.selectedLaneId = null;
      state.selectedPatternId = null;
    },
    clearCurrentTrack: (state) => {
      state.currentTrack = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tracks
      .addCase(fetchMapTracks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMapTracks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tracks = action.payload;
      })
      .addCase(fetchMapTracks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch track with tree
      .addCase(fetchTrackWithTree.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTrackWithTree.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTrack = action.payload;
        // Auto-select default map
        if (action.payload.maps?.length > 0) {
          const defaultMap = action.payload.maps.find((m: MapItem) => m.isDefault) || action.payload.maps[0];
          state.selectedMapId = defaultMap.id;
        }
      })
      .addCase(fetchTrackWithTree.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update card status
      .addCase(updateCardStatus.fulfilled, (state, action) => {
        const { cardId, status, actualMinutes } = action.payload;
        // Update card in the tree
        if (state.currentTrack?.maps) {
          for (const map of state.currentTrack.maps) {
            for (const lane of map.lanes) {
              for (const pattern of lane.patterns) {
                const card = pattern.cards.find(c => c.id === cardId);
                if (card) {
                  card.status = status;
                  if (actualMinutes !== undefined) card.actualMinutes = actualMinutes;
                  if (status === 'completed') card.completedAt = new Date().toISOString();
                  return;
                }
              }
            }
          }
        }
      })
      // Complete card
      .addCase(completeCard.fulfilled, (state, action) => {
        const { cardId, actualMinutes } = action.payload;
        // Check linear track cards first
        if (state.currentTrack?.cards) {
          const card = state.currentTrack.cards.find(c => c.id === cardId);
          if (card) {
            card.status = 'completed';
            card.completedAt = new Date().toISOString();
            if (actualMinutes !== undefined) card.actualMinutes = actualMinutes;
            return;
          }
        }
        // Check hierarchical track cards
        if (state.currentTrack?.maps) {
          for (const map of state.currentTrack.maps) {
            for (const lane of map.lanes) {
              for (const pattern of lane.patterns) {
                const card = pattern.cards.find(c => c.id === cardId);
                if (card) {
                  card.status = 'completed';
                  card.completedAt = new Date().toISOString();
                  if (actualMinutes !== undefined) card.actualMinutes = actualMinutes;
                  return;
                }
              }
            }
          }
        }
      })
      // Undo complete card
      .addCase(undoCompleteCard.fulfilled, (state, action) => {
        const { cardId } = action.payload;
        // Check linear track cards first
        if (state.currentTrack?.cards) {
          const card = state.currentTrack.cards.find(c => c.id === cardId);
          if (card) {
            card.status = 'pending';
            card.completedAt = undefined;
            return;
          }
        }
        // Check hierarchical track cards
        if (state.currentTrack?.maps) {
          for (const map of state.currentTrack.maps) {
            for (const lane of map.lanes) {
              for (const pattern of lane.patterns) {
                const card = pattern.cards.find(c => c.id === cardId);
                if (card) {
                  card.status = 'pending';
                  card.completedAt = undefined;
                  return;
                }
              }
            }
          }
        }
      })
      // Update pattern mastery
      .addCase(updatePatternMastery.fulfilled, (state, action) => {
        const { patternId, masteryState, nextReviewAt } = action.payload;
        if (state.currentTrack?.maps) {
          for (const map of state.currentTrack.maps) {
            for (const lane of map.lanes) {
              const pattern = lane.patterns.find(p => p.id === patternId);
              if (pattern) {
                pattern.masteryState = masteryState;
                pattern.nextReviewAt = nextReviewAt;
                pattern.lastPracticedAt = new Date().toISOString();
                pattern.practiceCount++;
                return;
              }
            }
          }
        }
      });
  },
});

export const { selectMap, selectLane, selectPattern, clearSelection, clearCurrentTrack, clearError } = mapSlice.actions;
export default mapSlice.reducer;
